import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SVGAssets } from '@business/assets/vectors';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FriendsFormProps } from '@business/interfaces/friendsForm/friendsForm';
import {
  FriendsFormData,
  friendsFormSchema
} from '@utils/validations/friends-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useAddFriendTypeMutation,
  useAddPeopleMutation,
  useGetFriendTypeQuery,
  useGetPeopleByIdQuery,
  useGetPeopleByNameQuery,
  useUpdatePeopleMutation
} from '@ports/internal-services/people/peopleSlice';
import { setAlertDetails } from '@ports/common/AlertStateSlice';
import { AlertProps } from '@business/interfaces/alert';
import { getGenderOptions } from '@utils/constants/globals';
import { getUserData } from '@ports/common/userStateSlice';
import { getToken } from '@ports/common/sessionStateSlice';
import { uploadToS3 } from '@utils/helpers/s3Upload';
import { useUserEmailExistCheck } from '@hooks/useUserEmailExistCheck';
import { createImageFileFromUrl } from '@utils/helpers/lib/photoHelpers';
import { Select } from '../Select';
import { Button } from '../Button';
import { Input } from '../Input';
import { SearchableSelectInput } from '../SearchableInput';
import { Alert } from '../Alert';
import { CustomDatePicker } from '../CustomDatePicker';
import { UploadPhotoModal } from '../MyProfileForm';
import { ExistingPersonFound } from './ExistingPersonFound';

interface MutualFriend {
  id: string;
  name: string;
}

interface EditingMutualFriend {
  index: number;
  id: string;
  name: string;
}

const formId = 'friendForm';

export const FriendsForm = ({
  isOpen,
  onClose,
  name,
  peopleFiltersHandler,
  createdPersonId,
  personToMove,
  setIsLoading,
  onPersonCreated
}: FriendsFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const submitBtnRef = useRef<HTMLButtonElement | null>(null);
  const userDetails = useSelector(getUserData);
  const token = useSelector(getToken);
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [mutualFriends, setMutualFriends] = useState<MutualFriend[]>([]);
  const [editingMutualFriend, setEditingMutualFriend] =
    useState<EditingMutualFriend | null>(null);
  const [showMutualFriend, setShowMutualFriend] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const [currentMutualFriendInput, setCurrentMutualFriendInput] = useState<{
    name: string;
    id: string;
  }>({ name: '', id: '' });
  const [isFriendNameSelected, setIsFriendNameSelected] =
    useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<AlertProps | null>(null);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(
    null
  );
  const [profilePicturePreview, setProfilePicturePreview] =
    useState<string>('');
  const [hasNameInteraction, setHasNameInteraction] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [isAllowingExistingPersonLink, setIsAllowingExistingPersonLink] =
    useState<boolean>(false);

  const [AddFriend] = useAddPeopleMutation();
  const [UpdateFriend] = useUpdatePeopleMutation();
  const [AddFriendType] = useAddFriendTypeMutation();
  const { data: friendTypes, refetch: refetchFriend } = useGetFriendTypeQuery(
    {}
  );

  const {
    handleEmailExistCheck,
    emailError,
    setEmailError,
    existingUser,
    setExistingUser
  } = useUserEmailExistCheck({ formId: formId });

  const { data: peopleDataForSearch, refetch: refetchPeople } =
    useGetPeopleByNameQuery(
      { type: 'friend', search: searchText },
      { skip: !searchText }
    );

  const friendTypeOptions = useMemo(() => {
    return (
      friendTypes?.data?.rows?.map((item: any) => ({
        label: item?.name,
        value: item?.id
      })) || []
    );
  }, [friendTypes?.data?.rows]);

  const { data: peopleData, refetch: refetchPeopleData } =
    useGetPeopleByIdQuery(
      {
        id: selectedPersonId
      },
      { skip: !selectedPersonId }
    );

  const shouldDisableFields = isFriendNameSelected && !!peopleData?.data?.type;
  useEffect(() => {
    if (selectedPersonId) {
      refetchPeopleData();
    }
  }, []);

  const friendSearchOptions = useMemo(() => {
    return (
      peopleDataForSearch?.data?.rows?.map((item: any) => ({
        id: item?.id,
        name: item?.name,
        profilePicUrl: item?.profileImage
      })) || []
    );
  }, [peopleDataForSearch?.data?.rows]);

  // Filter out selected friend from mutual friends options
  const mutualFriendOptions = useMemo(() => {
    return friendSearchOptions.filter(
      option => option?.id !== selectedPersonId
    );
  }, [friendSearchOptions, selectedPersonId]);

  useEffect(() => {
    refetchFriend();
  }, []);

  useEffect(() => {
    if (searchText) {
      refetchPeople();
    }
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    register,
    setFocus,
    trigger
  } = useForm<FriendsFormData>({
    resolver: yupResolver(friendsFormSchema),
    defaultValues: {
      friendsName: name ?? '',
      friendType: '',
      nickName: '',
      gender: '',
      birthday: '',
      birthPlace: '',
      mutualFriend: '',
      diary: '',
      isPrivate: false,
      email: ''
    },
    context: { skipConditionalRequired: shouldDisableFields }
  } as any);

  const MAX_CHARS = 2000;
  const diaryValue = watch('diary') || '';
  const genderValue = watch('gender');
  const currentLength = diaryValue?.length;

  // Bind name prop to friendsName field
  useEffect(() => {
    if (personToMove) {
      setValue('friendsName', personToMove.name);
      setSearchText(personToMove.name);
      setIsFriendNameSelected(true);
      setSelectedPersonId(personToMove.id);
    } else if (name) {
      setValue('friendsName', name);
      setSearchText(name);
    }
  }, [name, personToMove, setValue]);

  // Auto-focus on first validation error
  useEffect(() => {
    const firstError = Object.keys(errors)[0] as keyof FriendsFormData;
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  useEffect(() => {
    trigger(['friendType', 'gender']);
  }, [shouldDisableFields, trigger]);

  function afterSubmission() {
    setIsLoading(false);
    peopleFiltersHandler();
    reset();
    setMutualFriends([]);
    setShowMutualFriend(false);
    setProfilePictureFile(null);
    setProfilePicturePreview('');
    setExistingUser(null);
    setEmailError('');
    setIsAllowingExistingPersonLink(false);
    onClose(true);
  }

  const handleFormSubmit = async (data: FriendsFormData) => {
    // ✅ Use personToMove ID if available
    const peopleIdToUse =
      personToMove?.id || selectedPersonId || createdPersonId || '';

    if (!isAllowingExistingPersonLink) {
      const emailExists = await handleEmailExistCheck(data?.email); // Check email existence on form submit and set error if exists
      if (!emailExists) {
        return;
      }
    }

    setIsLoading(true);

    if (createdPersonId || personToMove) {
      // Update existing person
      const updateDataObj = {
        id: personToMove?.id || createdPersonId,
        updatePeople: {
          peopleId: peopleIdToUse,
          name: personToMove?.name || data.friendsName || name,
          type: 'friend',
          friendType: data.friendType,
          nickname: data.nickName,
          gender: data?.gender,
          dateOfBirth: data?.birthday ? data?.birthday : undefined,
          birthPlace: data?.birthPlace,
          mutualFriends: mutualFriends.map(item => ({
            id: item.id || '',
            name: item.name
          })),
          diary: data?.diary,
          isPrivate: isPrivate,
          email: data?.email
        }
      };

      try {
        const res: any = await UpdateFriend(updateDataObj);

        if (res?.data) {
          // If profile picture is selected, upload it
          if (profilePictureFile) {
            try {
              const idToken = token || '';
              if (!idToken) {
                throw new Error('Authentication token not found');
              }

              // Upload to S3
              const imageUrl = await uploadToS3(
                profilePictureFile,
                idToken,
                userDetails?.userId || '',
                'profile',
                '',
                {
                  userId: userDetails?.userId
                }
              );

              // Update people with profile image
              const updateImageDataObj = {
                id: personToMove?.id || createdPersonId,
                updatePeople: {
                  profileImage: imageUrl
                }
              };

              await UpdateFriend(updateImageDataObj);
            } catch (uploadError: any) {
              console.error('Profile picture upload error:', uploadError);
              dispatch(
                setAlertDetails({
                  alert: {
                    type: 'warning',
                    message: t(
                      'Friend updated but profile picture upload failed'
                    )
                  },
                  scope: 'people'
                })
              );
            }
          }

          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: 'Friend updated successfully'
              },
              scope: 'people'
            })
          );
          afterSubmission();
        } else {
          setIsLoading(false);
          setShowAlert(true);
          setAlertData({
            type: 'error',
            message: res?.error?.data?.data
          });
        }
      } catch (err: any) {
        setIsLoading(false);
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message
        });
      }
    } else {
      // Add new person
      const dataObj = {
        createPeople: {
          peopleId: peopleIdToUse,
          name: data.friendsName || name,
          type: 'friend',
          friendType: data.friendType,
          nickname: data.nickName,
          gender: data?.gender,
          dateOfBirth: data?.birthday ? data?.birthday : undefined,
          birthPlace: data?.birthPlace,
          mutualFriends: mutualFriends.map(item => ({
            id: item.id || '',
            name: item.name
          })),
          diary: data?.diary,
          isPrivate: isPrivate,
          email: data?.email
        }
      };

      try {
        const res: any = await AddFriend(dataObj);

        if (res?.data) {
          const newPeopleId = res.data.data.id;

          onPersonCreated?.(newPeopleId);

          // If profile picture is selected, upload it
          if (profilePictureFile && newPeopleId) {
            try {
              const idToken = token || '';
              if (!idToken) {
                throw new Error('Authentication token not found');
              }

              // Upload to S3
              const imageUrl = await uploadToS3(
                profilePictureFile,
                idToken,
                userDetails?.userId || '',
                'profile',
                '',
                {
                  userId: userDetails?.userId
                }
              );

              // Update people with profile image
              const updateDataObj = {
                id: newPeopleId,
                updatePeople: {
                  profileImage: imageUrl
                }
              };

              await UpdateFriend(updateDataObj);
            } catch (uploadError: any) {
              console.error('Profile picture upload error:', uploadError);
              dispatch(
                setAlertDetails({
                  alert: {
                    type: 'warning',
                    message: t('Friend added but profile picture upload failed')
                  },
                  scope: 'people'
                })
              );
            }
          }

          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: 'Friend added successfully'
              },
              scope: 'people'
            })
          );
          afterSubmission();
        } else {
          setIsLoading(false);
          setShowAlert(true);
          setAlertData({
            type: 'error',
            message: res?.error?.data?.data
          });
        }
      } catch (err: any) {
        setIsLoading(false);
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message
        });
      }
    }
  };

  const handleCancel = () => {
    reset();
    setEmailError('');
    setExistingUser(null);
    setIsAllowingExistingPersonLink(false);
    setMutualFriends([]);
    setCurrentMutualFriendInput({ name: '', id: '' });
    setShowMutualFriend(false);
    setSelectedPersonId('');
    setIsFriendNameSelected(false);
    setProfilePictureFile(null);
    setProfilePicturePreview('');
    setHasNameInteraction(false);
    if (profilePicturePreview) {
      URL.revokeObjectURL(profilePicturePreview);
    }
    onClose(false);
  };

  const handleAddMutualFriend = () => {
    if (!currentMutualFriendInput.name?.trim()) return;

    setMutualFriends(prev => [
      ...prev,
      {
        name: currentMutualFriendInput.name.trim(),
        id: currentMutualFriendInput.id || ''
      }
    ]);
    setCurrentMutualFriendInput({ name: '', id: '' });
    setShowMutualFriend(false);
  };

  const handleMutualFriendInputChange = (name: string, id?: string) => {
    setCurrentMutualFriendInput({
      name,
      id: id || ''
    });
  };

  const handleRemoveMutualFriend = (index: number) => {
    setMutualFriends(mutualFriends.filter((_, i) => i !== index));
  };

  const handleEditMutualFriend = (index: number) => {
    const friend = mutualFriends[index];
    if (!friend) return;

    setEditingMutualFriend({
      index,
      name: friend.name,
      id: friend.id || ''
    });

    // hide add input while editing
    setShowMutualFriend(false);
  };

  const handleMutualFriendEditChange = (name: string, id?: string) => {
    if (!editingMutualFriend) return;

    setEditingMutualFriend({
      ...editingMutualFriend,
      name,
      id: id || ''
    });
  };

  const handleUpdateMutualFriend = () => {
    if (!editingMutualFriend || !editingMutualFriend.name?.trim()) return;

    setMutualFriends(prev => {
      const updated = [...prev];

      updated[editingMutualFriend.index] = {
        name: editingMutualFriend.name.trim(),
        id: editingMutualFriend.id || ''
      };

      return updated;
    });

    setEditingMutualFriend(null);
  };

  const handleCancelMutualFriendEdit = () => {
    setEditingMutualFriend(null);
  };

  const searchMutualFriends = (value: string) => {
    setSearchText(value);
  };

  const handleLinkExistingPerson = useCallback(async () => {
    const linkDataObj = {
      ...existingUser
    };

    setIsAllowingExistingPersonLink(true);
    /* setValue('friendsName', linkDataObj?.name || '', {
      shouldValidate: true
    }); */
    if (linkDataObj?.profileImage) {
      const file = await createImageFileFromUrl(
        linkDataObj.profileImage,
        `${linkDataObj?.name.replace(/\s/g, '_')}.jpg`
      );
      setProfilePictureFile(file);
    }
    setExistingUser(null);
    setTimeout(() => {
      submitBtnRef.current?.click();
    }, 200);
  }, [existingUser, setExistingUser, setProfilePictureFile]);

  if (!isOpen) return null;

  const hasUnsavedFamilyMember =
    showMutualFriend && currentMutualFriendInput.name.trim() !== '';

  const hasUnsavedEdit = !!editingMutualFriend;

  const shouldBlockSubmit = hasUnsavedFamilyMember || hasUnsavedEdit;

  const addFriendTypeHandler = (value: string) => {
    const dataObj = {
      createFriendType: {
        name: value
      }
    };
    AddFriendType(dataObj)
      .then((res: any) => {
        if (res?.data) {
          refetchFriend();
        } else {
          console.log('Error adding friend type');
        }
      })
      .catch((err: any) => {
        console.log('Error adding friend type:', err);
      });
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[70]">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 relative animate-fadeIn">
        {/* Close Button */}
        <Button
          btnStyle="link"
          className="absolute right-4 top-4"
          onClick={handleCancel}>
          <SVGAssets.CloseIconGray className="w-4 h-4" />
        </Button>

        {showAlert && (
          <div>
            <Alert
              onClose={() => {
                setShowAlert(false);
              }}
              {...alertData}
              autoHide={true}
            />
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('peopleDetail.addFriend')}
          </h2>
          <p className="!text-xs text-gray-500 mt-1">
            {t('peopleDetail.addFriendDescription')}
          </p>
        </div>

        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          id={formId}
          className="space-y-4">
          {/* Friend Name */}
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('peopleDetail.friendsName')}
            <span className="text-red-500 ml-1">*</span>
          </label>

          <SearchableSelectInput
            name="friendsName"
            placeholder={t('peopleDetail.friendsName')}
            control={control}
            isWhiteTheme
            value={watch('friendsName')}
            options={friendSearchOptions}
            isDisabled={!!(name || personToMove)}
            onSearch={(value: string) => {
              setSearchText(value);
              if (value) {
                setHasNameInteraction(true);
              }
            }}
            onChange={({ id, name }) => {
              setValue('friendsName', name, { shouldValidate: true });
              setValue('friendId', id || '');
              setIsFriendNameSelected(!!id);
              setSelectedPersonId(id || '');
              if (name) {
                setHasNameInteraction(true);
              }
            }}
          />

          {errors.friendsName && (
            <p className="text-red-500 text-xs mt-1">
              {errors.friendsName.message}
            </p>
          )}

          {!selectedPersonId &&
            !isFriendNameSelected &&
            hasNameInteraction &&
            watch('friendsName') && (
              <>
                <div
                  className="flex w-full items-center cursor-pointer"
                  onClick={() => setShowUploadModal(true)}>
                  <SVGAssets.AddRoundIcon className="w-4 h-4" />
                  <span className="text-sm text-[#49712B] ml-2 font-semibold">
                    {t('addProfilePic')}
                  </span>
                </div>
                {profilePicturePreview && (
                  <div className="flex items-center gap-3 mt-2">
                    <img
                      src={profilePicturePreview}
                      alt="Profile preview"
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <Button
                      btnStyle="link"
                      type="button"
                      onClick={() => {
                        setProfilePictureFile(null);
                        if (profilePicturePreview) {
                          URL.revokeObjectURL(profilePicturePreview);
                        }
                        setProfilePicturePreview('');
                      }}
                      className="text-red-500 text-sm">
                      {t('remove')}
                    </Button>
                  </div>
                )}
              </>
            )}

          <div
            className={
              shouldDisableFields ? 'opacity-50 pointer-events-none' : ''
            }>
            <label className="block text-sm font-semibold text-[#353535] mb-1">
              {t('peopleDetail.friendType')}
              <span className="text-red-500 ml-1">*</span>
            </label>

            <Select
              name="friendType"
              options={[
                { label: t('peopleDetail.chooseFriend'), value: '' },
                ...friendTypeOptions
              ]}
              value={watch('friendType')}
              onChange={e =>
                setValue('friendType', e.target.value, { shouldValidate: true })
              }
              selectClassName="friend-select mb-3"
              disabled={shouldDisableFields}
              showAddNew
              onAddNewDone={val => {
                addFriendTypeHandler(val);
              }}
            />

            {errors.friendType && (
              <p className="text-red-500 text-xs mt-1">
                {errors.friendType.message}
              </p>
            )}

            <Input
              name="email"
              label={t('email')}
              placeholder={t('enterEmail')}
              control={control}
              isWhiteTheme
              type="text"
              disabled={shouldDisableFields}
              error={errors?.email?.message || emailError}
            />
          </div>

          {/* Nick Name & Gender */}
          <div
            className={`grid grid-cols-2 gap-4 ${shouldDisableFields ? 'opacity-50 pointer-events-none' : ''}`}>
            <Input
              name="nickName"
              label={t('peopleDetail.nickName')}
              placeholder={t('peopleDetail.enterNickName')}
              control={control}
              isWhiteTheme
              type="text"
              disabled={shouldDisableFields}
            />

            <div className="mt-2">
              <label className="block text-xs font-semibold text-[#353535] mb-1">
                {t('peopleDetail.gender')}
                {genderValue !== 'any' && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>

              <Select
                name="gender"
                options={getGenderOptions(t)}
                value={genderValue}
                onChange={e =>
                  setValue('gender', e.target.value, { shouldValidate: true })
                }
                selectClassName="friend-select"
                disabled={shouldDisableFields}
              />

              {errors.gender && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.gender.message}
                </p>
              )}
            </div>
          </div>

          {/* Birthday & Birth Place */}
          <div
            className={`grid grid-cols-2 gap-4 ${shouldDisableFields ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <Controller
                name="birthday"
                control={control}
                render={({ field }) => (
                  <CustomDatePicker
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    label={t('peopleDetail.birthday')}
                    maxDate={new Date()}
                    placeholder={t('peopleDetail.birthday')}
                    disabled={shouldDisableFields}
                  />
                )}
              />
            </div>

            <Input
              name="birthPlace"
              label={t('peopleDetail.birthPlace')}
              placeholder={t('peopleDetail.enterBirthPlace')}
              control={control}
              type="text"
              isWhiteTheme
              disabled={shouldDisableFields}
            />
          </div>
          {/* Add Mutual Friend Button */}
          <div
            className="flex w-full items-center cursor-pointer"
            onClick={() => {
              setShowMutualFriend(true);
            }}>
            <SVGAssets.AddRoundIcon className="w-4 h-4" />
            <span className="text-sm text-[#49712B] ml-2 font-semibold">
              {t('peopleDetail.addMutualFriend')}
            </span>
          </div>
          {/* Mutual Friend Input Fields */}
          <div className="space-y-3">
            {/* SAVED MUTUAL FRIENDS – ALWAYS VISIBLE */}
            {mutualFriends.map((friend, index) => (
              <div key={`friend-${index}`} className="space-y-2">
                {editingMutualFriend?.index === index ? (
                  /* EDIT MODE */
                  <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                    <SearchableSelectInput
                      label={t('peopleDetail.mutualFriend')}
                      isWhiteTheme
                      value={editingMutualFriend.name}
                      options={mutualFriendOptions}
                      onSearch={(value: string) => setSearchText(value)}
                      onChange={({ id, name }) =>
                        handleMutualFriendEditChange(name, id)
                      }
                      isSelectOnly={true}
                    />
                    <div className="flex items-center gap-1 pt-4">
                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={handleUpdateMutualFriend}>
                        <SVGAssets.GreenTick className="w-4 h-4" />
                      </Button>

                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={handleCancelMutualFriendEdit}>
                        <SVGAssets.CloseIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* VIEW MODE */
                  <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg">
                    <div>
                      <p className="text-[11px] text-gray-400">
                        {t('peopleDetail.mutualFriend')}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {friend.name}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={() => handleEditMutualFriend(index)}>
                        <SVGAssets.PencilIcon className="w-4 h-4" />
                      </Button>

                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={() => handleRemoveMutualFriend(index)}>
                        <SVGAssets.TrashIcon className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {/* ADD INPUT – controlled by showMutualFriend */}
            {showMutualFriend && (
              <div className="grid grid-cols-[1fr_auto] gap-4 items-center">
                <SearchableSelectInput
                  label={t('peopleDetail.mutualFriend')}
                  placeholder={t('peopleDetail.addMutualFriend')}
                  isWhiteTheme
                  value={currentMutualFriendInput.name}
                  options={mutualFriendOptions}
                  onSearch={searchMutualFriends}
                  onChange={({ id, name }) =>
                    handleMutualFriendInputChange(name, id)
                  }
                  isSelectOnly={true}
                />
                <div className="flex items-center gap-1 pt-4">
                  <Button
                    btnStyle="link"
                    type="button"
                    onClick={handleAddMutualFriend}>
                    <SVGAssets.GreenTick className="w-4 h-4" />
                  </Button>

                  <Button
                    btnStyle="link"
                    type="button"
                    onClick={() => {
                      setShowMutualFriend(false);
                      setCurrentMutualFriendInput({ name: '', id: '' });
                    }}>
                    <SVGAssets.CloseIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Diary */}
          <div
            className={
              shouldDisableFields ? 'opacity-50 pointer-events-none' : ''
            }>
            <div className="flex justify-between w-full mb-2">
              <label className="block text-sm font-semibold text-[#353535]">
                {t('peopleDetail.diary')}
              </label>

              {/* Private Toggle */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#49712B] mr-2">
                  {t('peopleDetail.private')}
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    title="is private"
                    type="checkbox"
                    checked={isPrivate}
                    onChange={e => setIsPrivate(e.target.checked)}
                    className="sr-only peer"
                    disabled={shouldDisableFields}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-[#7F9D48] after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-all peer-checked:after:translate-x-full" />
                </label>
              </div>
            </div>

            <div>
              <textarea
                {...register('diary')}
                maxLength={MAX_CHARS}
                rows={3}
                placeholder={t('peopleDetail.desc')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
               focus:outline-none focus:ring-1 focus:ring-[#8d8d8d] resize-none"
                disabled={shouldDisableFields}
              />
              <div className=" text-xs text-gray-400 flex justify-end">
                {currentLength}/{MAX_CHARS}
              </div>
            </div>

            {errors.diary && (
              <p className="text-red-500 text-xs mt-1">
                {errors.diary.message}
              </p>
            )}
          </div>

          {shouldBlockSubmit && (
            <div className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
              {t('peopleDetail.familyMemberChangesWarning')}
            </div>
          )}
          {/* Action Buttons */}
          <div className="flex justify-center items-center gap-3 pt-4">
            <Button
              btnStyle="link"
              className="px-6 py-2 !bg-[#F9FEE9] border-[var(--na-primary-color)] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
              onClick={handleCancel}>
              {t('cancel')}
            </Button>
            <Button
              ref={submitBtnRef}
              btnStyle="primary"
              type="submit"
              isDisabled={shouldBlockSubmit}
              className="px-6 rounded-lg py-2">
              {t('peopleDetail.add')}
            </Button>
          </div>
        </form>
      </div>
      {showUploadModal && (
        <UploadPhotoModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          userId={userDetails?.userId || ''}
          isPeopleProfilePic={false}
          skipUpload={true}
          onFileSelect={(file: File) => {
            setProfilePictureFile(file);
            const previewUrl = URL.createObjectURL(file);
            setProfilePicturePreview(previewUrl);
            setShowUploadModal(false);
          }}
        />
      )}

      {existingUser && (
        <ExistingPersonFound
          existingUser={existingUser}
          handleLinkExistingPerson={handleLinkExistingPerson}
          handleUseDifferentEmail={() => {
            setIsAllowingExistingPersonLink(false);
            setExistingUser(null);
          }}
        />
      )}
    </div>
  );
};
